import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@chakra-ui/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { antifragilityEndpoints } from '../../../endpoints/antifragility';
import { IAFISubMetric } from '../../../models/afi';
import { AFI_COMPONENT_LABELS, getAFIColor } from '../../constants/afi';
import { ChartEmptyState } from '../AthleteInjuryCharts/charts/_shared/ChartEmptyState';
import AFIScoreBadge from './AFIScoreBadge';
import AFITrendArrow from './AFITrendArrow';
import CustomTable from '../../../shared/CustomTable/CustomTable';
import styles from './AFIComponentDetail.module.scss';

interface AFIComponentDetailProps {
  teamId: string;
  athleteId: string;
  component: 'A' | 'B' | 'C';
}

const STATUS_COLORS: Record<IAFISubMetric['status'], string> = {
  ottimo:     '#1a7a4a',
  normale:    '#4d4d4d',
  attenzione: '#b45309',
  critico:    '#991b1b',
};

const STATUS_BG: Record<IAFISubMetric['status'], string> = {
  ottimo:     '#e6f4ed',
  normale:    '#f0f0f0',
  attenzione: '#fef3c7',
  critico:    '#fee2e2',
};

const STATUS_LABELS: Record<IAFISubMetric['status'], string> = {
  ottimo:     'Ottimo',
  normale:    'Normale',
  attenzione: 'Attenzione',
  critico:    'Critico',
};

const AFIComponentDetail: React.FC<AFIComponentDetailProps> = ({
  teamId,
  athleteId,
  component,
}) => {
  const [selectedMetric, setSelectedMetric] = useState<string>('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['afi', 'component', athleteId, teamId, component],
    queryFn: async () => {
      const { data } = await antifragilityEndpoints.getAFIComponent(
        athleteId,
        component,
        { teamId }
      );
      return data;
    },
    enabled: !!athleteId && !!teamId && !!component,
  });

  // Imposta metrica di default al primo caricamento
  const resolvedMetric =
    selectedMetric ||
    (data?.subMetrics?.[0]?.id ?? '');

  if (isLoading) {
    return (
      <div className={styles.centered}>
        <Spinner size="lg" color="#5BC9E0" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={styles.error}>
        Impossibile caricare dati AFI
      </div>
    );
  }

  const activeMetric = data.subMetrics.find((m) => m.id === resolvedMetric);
  const chartData =
    activeMetric?.history.filter((p) => p.value !== null).map((p) => ({
      weekLabel: p.weekLabel,
      value: p.value as number,
    })) ?? [];

  const barFill = getAFIColor(data.score);

  return (
    <div className={styles.wrapper}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h3 className={styles.componentTitle}>
            {AFI_COMPONENT_LABELS[component]}
          </h3>
          <span className={styles.componentLetter}>Componente {component}</span>
        </div>
        <AFIScoreBadge score={data.score} size="md" showLabel />
      </div>

      {/* Tabella sub-metriche */}
      {data.subMetrics.length > 0 ? (
        <div className={styles.tableWrapper}>
          <CustomTable
            headers={['Nome', 'Valore', 'Trend', 'Status']}
            data={data.subMetrics}
            fields={{
              Nome: {
                key: 'label',
                customComponent: (_: any, metric: IAFISubMetric) => (
                  <span className={styles.metricName}>{metric.label}</span>
                ),
              },
              Valore: {
                key: 'value',
                customComponent: (_: any, metric: IAFISubMetric) => (
                  <span className={styles.metricValue}>
                    {metric.value !== null ? (
                      <span>
                        {metric.value}
                        {metric.unit && <span className={styles.unit}> {metric.unit}</span>}
                      </span>
                    ) : (
                      <span className={styles.noData}>N/D</span>
                    )}
                  </span>
                ),
              },
              Trend: {
                key: 'trend',
                customComponent: (_: any, metric: IAFISubMetric) => (
                  <AFITrendArrow trend={metric.trend} delta={0} />
                ),
              },
              Status: {
                key: 'status',
                customComponent: (_: any, metric: IAFISubMetric) => (
                  <span
                    className={styles.statusBadge}
                    style={{
                      color: STATUS_COLORS[metric.status],
                      backgroundColor: STATUS_BG[metric.status],
                    }}
                  >
                    {STATUS_LABELS[metric.status]}
                  </span>
                ),
              },
            }}
          />
        </div>
      ) : (
        <p className={styles.noMetrics}>Nessuna sub-metrica disponibile</p>
      )}

      {/* Selector sub-metrica */}
      {data.subMetrics.length > 0 && (
        <div className={styles.metricSelector}>
          <span className={styles.selectorLabel}>Storico:</span>
          {data.subMetrics.map((metric) => (
            <button
              key={metric.id}
              className={`${styles.pill} ${resolvedMetric === metric.id ? styles.pillActive : ''}`}
              onClick={() => setSelectedMetric(metric.id)}
            >
              {metric.label}
            </button>
          ))}
        </div>
      )}

      {/* Chart storico */}
      {data.subMetrics.length > 0 && (
        <div className={styles.chartSection}>
          {activeMetric && (
            <div className={styles.chartTitle}>
              {activeMetric.label}
              {activeMetric.unit && (
                <span className={styles.chartUnit}> ({activeMetric.unit})</span>
              )}
            </div>
          )}
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={chartData}
                margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e7" vertical={false} />
                <XAxis
                  dataKey="weekLabel"
                  tick={{ fill: '#4d4d4d', fontSize: 11, fontFamily: "'Satoshi', sans-serif" }}
                />
                <YAxis
                  tick={{ fill: '#4d4d4d', fontSize: 11, fontFamily: "'Satoshi', sans-serif" }}
                />
                <Tooltip
                  contentStyle={{ fontFamily: "'Satoshi', sans-serif", fontSize: 12 }}
                />
                <Bar dataKey="value" fill={barFill} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ChartEmptyState />
          )}
        </div>
      )}
    </div>
  );
};

export default AFIComponentDetail;
